import { useEffect } from 'react'
import { useForm, Controller, useFieldArray, type Control, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, Divider, Typography, IconButton, Box, Paper,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createWarehouse, updateWarehouse, getWarehouse, listWarehouseWorkPoints,
  type WarehouseListItem, type SaveWarehousePayload,
} from '../../api/depozite'
import { errorMessage } from '../../api/client'

const locationSchema = z.object({ id: z.string().nullable(), code: z.string().min(1, 'Câmp obligatoriu'), name: z.string().optional() })
const zoneSchema = z.object({
  id: z.string().nullable(),
  code: z.string().min(1, 'Câmp obligatoriu'),
  name: z.string().min(1, 'Câmp obligatoriu'),
  type: z.enum(['Receptie', 'DepozitareMateriiPrime', 'ProductieWip', 'ProduseFinite', 'ExpeditieStaging', 'Scrap', 'Altele']),
  materialFilter: z.union([z.enum(['PS', 'PP', 'Alu', 'Altele']), z.literal('')]).optional(),
  locations: z.array(locationSchema),
})
const schema = z.object({
  code: z.string().min(1, 'Câmp obligatoriu').max(32),
  name: z.string().min(1, 'Câmp obligatoriu').max(128),
  workPointId: z.string().min(1, 'Selectați punct de lucru'),
  zones: z.array(zoneSchema),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { code: '', name: '', workPointId: '', zones: [] }

const ZONE_TYPES: { value: FormValues['zones'][number]['type']; label: string }[] = [
  { value: 'Receptie', label: 'Recepție' },
  { value: 'DepozitareMateriiPrime', label: 'Depozitare materii prime' },
  { value: 'ProductieWip', label: 'Producție / WIP' },
  { value: 'ProduseFinite', label: 'Produse finite' },
  { value: 'ExpeditieStaging', label: 'Expediție / staging' },
  { value: 'Scrap', label: 'Scrap' },
  { value: 'Altele', label: 'Altele' },
]

export default function WarehouseFormDialog({ warehouse, onClose }: { warehouse: WarehouseListItem | null | 'new'; onClose: () => void }) {
  const open = warehouse !== null
  const editing = warehouse !== null && warehouse !== 'new'
  const qc = useQueryClient()

  const { data: workPoints } = useQuery({ queryKey: ['stocuri', 'depozite', 'puncte-lucru'], queryFn: listWarehouseWorkPoints, enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['stocuri', 'depozit', editing ? (warehouse as WarehouseListItem).id : null],
    queryFn: () => getWarehouse((warehouse as WarehouseListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const zones = useFieldArray({ control, name: 'zones' })

  useEffect(() => {
    if (editing && detail) {
      reset({
        code: detail.code, name: detail.name, workPointId: detail.workPointId,
        zones: detail.zones.map((z) => ({
          id: z.id, code: z.code, name: z.name, type: z.type, materialFilter: z.materialFilter ?? '',
          locations: z.locations.map((l) => ({ id: l.id, code: l.code, name: l.name ?? '' })),
        })),
      })
    } else if (warehouse === 'new') {
      reset(EMPTY)
    }
  }, [warehouse, editing, detail, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveWarehousePayload = {
        code: values.code, name: values.name, workPointId: values.workPointId,
        zones: values.zones.map((z) => ({
          id: z.id, code: z.code, name: z.name, type: z.type, materialFilter: z.materialFilter || null,
          locations: z.locations.map((l) => ({ id: l.id, code: l.code, name: l.name || null })),
        })),
      }
      if (editing) return updateWarehouse((warehouse as WarehouseListItem).id, payload)
      return createWarehouse(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stocuri', 'depozite'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare depozit' : 'Depozit nou'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={2}>
              <Controller name="code" control={control} render={({ field }) => (
                <TextField {...field} label="Cod" fullWidth error={!!errors.code} helperText={errors.code?.message} />
              )} />
              <Controller name="name" control={control} render={({ field }) => (
                <TextField {...field} label="Denumire" fullWidth error={!!errors.name} helperText={errors.name?.message} />
              )} />
              <Controller name="workPointId" control={control} render={({ field }) => (
                <TextField {...field} select label="Punct de lucru" fullWidth error={!!errors.workPointId} helperText={errors.workPointId?.message}>
                  {workPoints?.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
                </TextField>
              )} />
            </Stack>

            <Divider><Typography variant="overline" color="text.secondary">Zone ({zones.fields.length})</Typography></Divider>
            <Stack spacing={1.5}>
              {zones.fields.map((f, i) => (
                <ZoneRow key={f.id} index={i} control={control} errors={errors} onRemove={() => zones.remove(i)} />
              ))}
              <Box>
                <Button size="small" startIcon={<Add />}
                  onClick={() => zones.append({ id: null, code: '', name: '', type: 'Receptie', materialFilter: '', locations: [] })}>
                  Adaugă zonă
                </Button>
              </Box>
            </Stack>

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

/**
 * Subcomponent separat, nu inline în `.map()` — `useFieldArray` pentru locațiile
 * unei zone e un hook și nu poate fi apelat într-o buclă (același motiv pentru
 * care `ModuleAccordion` e subcomponent în `RoleFormDialog.tsx`).
 */
function ZoneRow({ index, control, errors, onRemove }: {
  index: number; control: Control<FormValues>; errors: FieldErrors<FormValues>; onRemove: () => void
}) {
  const locations = useFieldArray({ control, name: `zones.${index}.locations` })
  const zoneErrors = errors.zones?.[index]

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
        <Controller name={`zones.${index}.code`} control={control} render={({ field }) => (
          <TextField {...field} label="Cod zonă" sx={{ width: 140 }} error={!!zoneErrors?.code} helperText={zoneErrors?.code?.message} />
        )} />
        <Controller name={`zones.${index}.name`} control={control} render={({ field }) => (
          <TextField {...field} label="Denumire zonă" fullWidth error={!!zoneErrors?.name} helperText={zoneErrors?.name?.message} />
        )} />
        <Controller name={`zones.${index}.type`} control={control} render={({ field }) => (
          <TextField {...field} select label="Tip zonă" sx={{ width: 220 }} error={!!zoneErrors?.type} helperText={zoneErrors?.type?.message}>
            {ZONE_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
        )} />
        <Controller name={`zones.${index}.materialFilter`} control={control} render={({ field }) => (
          <TextField {...field} select label="Material (opțional)" sx={{ width: 160 }}>
            <MenuItem value="">—</MenuItem>
            <MenuItem value="PS">PS</MenuItem>
            <MenuItem value="PP">PP</MenuItem>
            <MenuItem value="Alu">Alu</MenuItem>
            <MenuItem value="Altele">Altele</MenuItem>
          </TextField>
        )} />
        <IconButton onClick={onRemove}><DeleteOutlined fontSize="small" /></IconButton>
      </Stack>

      <Stack spacing={1} sx={{ mt: 1.5, pl: 2 }}>
        <Typography variant="caption" color="text.secondary">Locații ({locations.fields.length})</Typography>
        {locations.fields.map((f, li) => (
          <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
            <Controller name={`zones.${index}.locations.${li}.code`} control={control} render={({ field }) => (
              <TextField {...field} label="Cod locație" size="small" sx={{ width: 140 }}
                error={!!zoneErrors?.locations?.[li]?.code} helperText={zoneErrors?.locations?.[li]?.code?.message} />
            )} />
            <Controller name={`zones.${index}.locations.${li}.name`} control={control} render={({ field }) => (
              <TextField {...field} label="Denumire (opțional)" size="small" fullWidth />
            )} />
            <IconButton size="small" onClick={() => locations.remove(li)}><DeleteOutlined fontSize="small" /></IconButton>
          </Stack>
        ))}
        <Box>
          <Button size="small" startIcon={<Add />} onClick={() => locations.append({ id: null, code: '', name: '' })}>
            Adaugă locație
          </Button>
        </Box>
      </Stack>
    </Paper>
  )
}
